import { z } from 'zod';
/**
 * 패밀리 사이트 = 사용자 채팅 사이드바에 노출되는 외부/사내 사이트 바로가기 목록.
 * 어드민에서 CRUD 로 관리하고, 인증된 사용자가 사이드바에서 클릭으로 이동.
 */
export const FamilySiteLogoTypeSchema = z.enum(['emoji', 'lucide', 'image']);
/** 로고 업로드 시 허용 MIME (branding 과 동일). */
export const FAMILY_SITE_LOGO_ALLOWED_MIMES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
];
/** 로고 파일 크기 상한 (2MB). */
export const FAMILY_SITE_LOGO_MAX_BYTES = 2 * 1024 * 1024;
/**
 * 사이드바용 공개 형태. image 타입은 `logoUrl` 이 세팅됨 (서버에서 합성).
 * 인증된 사용자만 호출 가능 (`GET /api/family-sites`).
 */
export const PublicFamilySiteSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    url: z.string(),
    openInNewTab: z.boolean(),
    logoType: FamilySiteLogoTypeSchema,
    logoEmoji: z.string().nullable(),
    logoLucideName: z.string().nullable(),
    /** image 타입일 때만 `/api/family-sites/:id/logo?v=<unix_ts>`. 그 외 null. */
    logoUrl: z.string().nullable(),
});
export const PublicFamilySiteListSchema = z.object({
    sites: z.array(PublicFamilySiteSchema),
});
/** 어드민 화면용 — public + 운영 메타. */
export const AdminFamilySiteSchema = PublicFamilySiteSchema.extend({
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminFamilySiteListSchema = z.object({
    sites: z.array(AdminFamilySiteSchema),
});
const NAME_MAX = 60;
const URL_MAX = 500;
const EMOJI_MAX = 16;
const LUCIDE_NAME_MAX = 80;
/**
 * 로고 입력 — logoType 에 따라 해당 필드를 채워서 보낸다.
 *   emoji  → logoEmoji 필수
 *   lucide → logoLucideName 필수
 *   image  → 별도 upload-logo 엔드포인트 호출 (생성 시에는 emoji 임시 셋 권장)
 */
const LogoFieldsSchema = z
    .object({
    logoType: FamilySiteLogoTypeSchema,
    logoEmoji: z.string().trim().min(1).max(EMOJI_MAX).nullable().optional(),
    logoLucideName: z.string().trim().min(1).max(LUCIDE_NAME_MAX).nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.logoType === 'emoji' && !v.logoEmoji) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['logoEmoji'],
            message: '이모지를 입력해 주세요.',
        });
    }
    if (v.logoType === 'lucide' && !v.logoLucideName) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['logoLucideName'],
            message: '아이콘을 선택해 주세요.',
        });
    }
});
export const CreateFamilySiteRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(NAME_MAX),
    url: z.string().trim().url().max(URL_MAX),
    openInNewTab: z.boolean().optional(),
})
    .and(LogoFieldsSchema);
export const UpdateFamilySiteRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(NAME_MAX).optional(),
    url: z.string().trim().url().max(URL_MAX).optional(),
    openInNewTab: z.boolean().optional(),
    logoType: FamilySiteLogoTypeSchema.optional(),
    logoEmoji: z.string().trim().min(1).max(EMOJI_MAX).nullable().optional(),
    logoLucideName: z.string().trim().min(1).max(LUCIDE_NAME_MAX).nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.logoType === 'emoji' && v.logoEmoji === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['logoEmoji'],
            message: '이모지를 입력해 주세요.',
        });
    }
    if (v.logoType === 'lucide' && v.logoLucideName === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['logoLucideName'],
            message: '아이콘을 선택해 주세요.',
        });
    }
});
export const ReorderFamilySitesRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
//# sourceMappingURL=family-sites.js.map